import { Space, Tooltip } from 'antd';
import { ReactElement } from 'react';
import { EuroCircleFilled, EuroCircleOutlined, WarningFilled } from '@ant-design/icons';
import { User } from 'store/session/user';
import { hasPermissions } from 'config/roles/hasPermissions';
import { ColumnsType, ColumnGroupType, ColumnType } from 'antd/lib/table';
import { enumToText } from 'core/utils/enum-to-text';
import { Declaration, DeclarationSortParameters, GetDeclarationsSortParameter } from 'store/declarations/declaration';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { getMessageTypeByDeclaration } from 'views/declarations/utils/declaration-utils';
import { FlexDiv, SpanEllipsis } from './components/DeclarationsTable.styled';
import { gvmsNameLabels } from '../../../store/declarations/uk/gvms-declaration';
import { tsdNameLabels } from '../../../store/declarations/ireland/temporary-storage-declaration';
import Consignor from './components/column-components/Consignor';
import Consignee from './components/column-components/Consignee';
import SubmittedBy from './components/column-components/SubmittedBy';
import Edited from './components/column-components/Edited';
import Items from './components/column-components/Items';
import DeclarationType from './components/column-components/DeclarationType';
import Status from './components/column-components/Status';
import Actions from './components/column-components/Actions';
import Mrn from './components/column-components/Mrn';
import getMrnOrDucr, { getDucr } from './utils/getMrnOrDucr';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import {
    getIrelandNotificationMessage,
    getIrelandNotificationTooltip,
} from 'views/declarations/common/declaration-errors-drawer/declaration-notifications/ais/utils';

/** Every group action is keyed by the source manifest id the group was generated from. */
export interface GroupActionEvents {
    onArchiveGroup?: (sourceManifestId: string) => void;
    onUnarchiveGroup?: (sourceManifestId: string) => void;
    onDeleteGroup?: (sourceManifestId: string) => void;
    onDuplicateGroup?: (sourceManifestId: string) => void;
}

export interface ActionEvents {
    onDuplicate?: (record: any) => void;
    onArchive?: (recordIds: string[]) => void;
    onPrint?: (recordId: string) => void;
    onDelete?: (record: any) => void;
    onUnarchive?: (record: any) => void;
    onBarcode?: (record: any) => void;
    onCoreTemplateToggle?: (record: any) => void;
    filterDataColumns?: (dataColumns: ColumnsType<any>) => (ColumnGroupType<any> | ColumnType<any>)[];
    groupActions?: GroupActionEvents;
}

export enum DeclarationColumnsTitles {
    ID = 'Id',
    TYPE = 'Type',
    MRN = 'MRN',
    STATUS = 'Status',
    ITEMS = 'Items',
    CONSIGNOR = 'Consignor',
    CONSIGNEE = 'Consignee',
    SUBMITTED_BY = 'Submitted By',
    EDITED = 'Edited',
    ACTIONS = 'Actions',
}

export const getDeclarationTitle = (declaration: Declaration | undefined) => {
    if (!declaration) return '';
    const internalType = getInternalType(declaration);
    let messageType = getMessageTypeByDeclaration(declaration);

    if (declaration?.preBoardingNotification || declaration?.entrySummaryDeclaration) return internalType;

    if (declaration?.gvmsDeclaration) {
        const foundVal = gvmsNameLabels.find((label) => label.key === messageType)?.value;
        messageType = foundVal || 'Unknown';
    } else if (declaration?.ieImportTemporaryStorageDeclaration) {
        const foundVal = tsdNameLabels.find((label) => label.key === messageType)?.value;
        messageType = foundVal || 'Unknown';
    }

    return `${internalType} (${messageType})`;
};

export const getInternalType = (declaration: Declaration) => {
    if (declaration.declarationInternalType === 'IMPORT_NEW') {
        return enumToText('IMPORT');
    }
    return enumToText(declaration.declarationInternalType);
};

const getLatestIrelandNotification = (declaration: Declaration | undefined) =>
    declaration?.irelandImportDeclaration?.notifications?.[0] ??
    declaration?.irelandH7ImportDeclaration?.notifications?.[0];

export const getDeclarationType = (declaration: Declaration) => {
    const country = declaration.declarationExternalEntity === DeclarationExternalEntity.REVENUE ? 'Ireland' : 'UK';
    return (
        <FlexDiv>
            <SpanEllipsis>{country}</SpanEllipsis>
            <SpanEllipsis>{getDeclarationTitle(declaration)}</SpanEllipsis>
        </FlexDiv>
    );
};

export const declarationDataColumns: ColumnsType<Declaration> = [
    {
        key: 'notifications',
        width: 1,
        render: (_, record) => {
            const hasVatAndDutyDifferenceNotification = record?.taxCalculationDiffers;
            const hasAcknowledgedVatAndDutyNotification = false; // Note: This will be handled in a followup
            const hasTransportDetailsDiscrepancies = record?.manifestDetailsMismatch;
            const irelandMessage = getIrelandNotificationMessage(getLatestIrelandNotification(record));

            const notificationIcons: ReactElement[] = [];

            if (hasVatAndDutyDifferenceNotification) {
                const vatAndDutyNotificationIcon = hasAcknowledgedVatAndDutyNotification ? (
                    <EuroCircleOutlined
                        style={{
                            color: 'var(--gray-6)',
                        }}
                    />
                ) : (
                    <EuroCircleFilled
                        style={{
                            color: 'var(--error-normal)',
                        }}
                    />
                );
                notificationIcons.push(
                    <Tooltip title="VAT and Duty Discrepancies">{vatAndDutyNotificationIcon}</Tooltip>
                );
            }

            if (hasTransportDetailsDiscrepancies) {
                notificationIcons.push(
                    <Tooltip title="Transport details differ from manifest">
                        <WarningFilled style={{ color: 'var(--warning-normal)' }} />
                    </Tooltip>
                );
            }

            if (irelandMessage) {
                notificationIcons.push(
                    <Tooltip title={getIrelandNotificationTooltip(irelandMessage)}>
                        <WarningFilled style={{ color: 'var(--warning-normal)' }} />
                    </Tooltip>
                );
            }

            return <Space size={8}>{notificationIcons}</Space>;
        },
    },
    {
        title: DeclarationColumnsTitles.TYPE,
        dataIndex: DeclarationSortParameters.MESSAGE_TYPE,
        key: DeclarationSortParameters.EXTERNAL_ENTITY,
        sorter: true,
        render: (text: string, record: Declaration) => {
            return <DeclarationType dataTestId="declaration-type-row" record={record} />;
        },
    },
    {
        title: DeclarationColumnsTitles.MRN,
        dataIndex: 'mrn',
        key: 'MRN',
        render: (mrn: string) => {
            return <Mrn mrn={mrn} dataTestId="declaration-mrn-row" />;
        },
    },

    {
        title: DeclarationColumnsTitles.STATUS,
        dataIndex: DeclarationSortParameters.STATUS,
        sorter: true,
        key: DeclarationSortParameters.STATUS,
        render: (text: string, record: Declaration) => {
            return <Status declaration={record} dataTestId="declaration-status-row" />;
        },
    },
    {
        title: DeclarationColumnsTitles.ITEMS,
        key: 'NUM_ITEMS',
        render: (record?: Declaration) => {
            return <Items declaration={record} dataTestId="declaration-num-items-row" />;
        },
    },
    {
        title: DeclarationColumnsTitles.CONSIGNOR,
        key: 'CONSIGNOR',
        render: (record?: Declaration) => {
            return <Consignor dataTestId="declaration-consignor-row" declaration={record} />;
        },
    },
    {
        title: DeclarationColumnsTitles.CONSIGNEE,
        key: 'CONSIGNEE',
        render: (record?: Declaration) => {
            return <Consignee dataTestId="declaration-consignee-row" declaration={record} />;
        },
    },
    {
        title: DeclarationColumnsTitles.SUBMITTED_BY,
        key: 'SUBMITTED_BY',
        render: (record?: Declaration) => {
            return <SubmittedBy dataTestId="declaration-submitted-by-row" declaration={record} />;
        },
    },
    {
        title: DeclarationColumnsTitles.EDITED,
        key: GetDeclarationsSortParameter.UPDATED_AT,
        sorter: true,
        render: (record?: Declaration) => {
            return <Edited dataTestId="declaration-edited-row" declaration={record} />;
        },
    },
];

export const getActionsColumn = (actions: ActionEvents, user: User | undefined) => [
    {
        title: 'Actions',
        key: 'actions',
        width: 60,
        onCell: () => ({
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
            },
        }),
        render: (_: string, record: Declaration) => {
            const showActions = hasPermissions(user, 'declarations', 'UPDATE');
            const canArchive = hasPermissions(user, 'declarations', 'ARCHIVE');
            const canDelete = hasPermissions(user, 'declarations', 'DELETE');

            return (
                <Actions
                    showActions={showActions}
                    canArchive={canArchive}
                    canDelete={canDelete}
                    declaration={record}
                    actions={actions}
                />
            );
        },
    },
];

const hasAnyNotification = (declarations: Declaration[] | undefined) =>
    declarations?.some(
        (d) =>
            d.taxCalculationDiffers ||
            d.manifestDetailsMismatch ||
            getIrelandNotificationMessage(getLatestIrelandNotification(d))
    ) ?? false;

export const buildColumns = (
    dataColumns: ColumnsType<Declaration>,
    actions: ActionEvents,
    user: User | undefined,
    declarations?: Declaration[]
) => {
    const { filterDataColumns, ...actionButtons } = actions;

    let _dataColumns = !!filterDataColumns ? filterDataColumns(dataColumns) : dataColumns;

    if (!hasAnyNotification(declarations)) {
        _dataColumns = (_dataColumns as (ColumnGroupType<any> | ColumnType<any>)[]).filter(
            (col: ColumnGroupType<any> | ColumnType<any>) => col.key !== 'notifications'
        );
    }

    return Object.values(actionButtons).some((action) => action)
        ? [..._dataColumns, ...getActionsColumn(actionButtons, user)]
        : [..._dataColumns];
};

const ducrColumn: ColumnType<Declaration> = {
    title: 'DUCR',
    key: 'DUCR',
    render: (_, record) => {
        const dataTestId = 'declaration-ducr-row';
        const ducr = getDucr(record);

        if (!ducr) {
            return <EmptyCell dataTestId={dataTestId} />;
        }

        return (
            <TooltipWithCopy dataTestId={dataTestId} copyText={ducr}>
                {ducr}
            </TooltipWithCopy>
        );
    },
};

const mrnOrDucrColumn: ColumnType<Declaration> = {
    title: `${DeclarationColumnsTitles.MRN} / DUCR`,
    key: 'MRN_DUCR',
    render: (_, record) => <Mrn mrn={getMrnOrDucr(record)} dataTestId="declaration-mrn-or-ducr-row" />,
};

const indexOfKey = (columns: ColumnsType<Declaration>, key: string) =>
    columns.findIndex((column) => column.key === key);

const insertAfterKey = (
    columns: ColumnsType<Declaration>,
    key: string,
    newColumn: ColumnType<Declaration>
): ColumnsType<Declaration> => {
    const index = indexOfKey(columns, key);
    const insertAt = index === -1 ? columns.length : index + 1;

    return [...columns.slice(0, insertAt), newColumn, ...columns.slice(insertAt)];
};

const replaceByKey = (
    columns: ColumnsType<Declaration>,
    key: string,
    newColumn: ColumnType<Declaration>
): ColumnsType<Declaration> => columns.map((column) => (column.key === key ? newColumn : column));

const dataColumnsByDucrMode = {
    none: declarationDataColumns,
    // Its own column next to the MRN one.
    separate: insertAfterKey(declarationDataColumns, 'MRN', ducrColumn),
    // One column that takes the place of the MRN one.
    combined: replaceByKey(declarationDataColumns, 'MRN', mrnOrDucrColumn),
};

interface ColumnOptions {
    ducr?: keyof typeof dataColumnsByDucrMode;
}

const getColumns = (
    actions: ActionEvents,
    user: User | undefined,
    declarations?: Declaration[],
    { ducr = 'none' }: ColumnOptions = {}
) => buildColumns(dataColumnsByDucrMode[ducr], actions, user, declarations);

export default getColumns;
